import { Grid, IconButton, Tooltip } from '@mui/material';
import type { Editor } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';

import { useState } from 'react';

import { DeleteOutlineOutlined, EditOutlined } from '@mui/icons-material';

import { Or2Card } from '@/components/Surface/Or2Card';

import { Or2Link } from '@/components/navigation/Or2Link';

import { AnchorDialog } from '../menu/anchor-button/AnchorDialog';
import type { RootOwner } from '../menu/anchor-button/file-selector/FileSelectorDialog';

interface Props {
  editor: Editor | null;
  rootOwner?: RootOwner;
  disableFileSelector?: boolean;
}

export const Or2BubbleMenu = ({
  editor,
  rootOwner,
  disableFileSelector = false,
}: Props) => {
  const [anchorDialogOpen, setAnchorDialogOpen] = useState(false);
  // We store the link in its own state to prevent the editor from getting out of sync with the bubble menu
  const [link, setLink] = useState<string | null>(null);

  return (
    <div>
      <BubbleMenu
        editor={editor ?? undefined}
        options={{
          strategy: 'absolute',
          placement: 'top',
        }}
        shouldShow={({ editor, from, to }) => {
          const isActive = editor.isActive('link');

          if (!isActive) {
            setLink(null);
          }

          const editorLink = editor.getAttributes('link').href;

          if (editorLink) {
            setLink(editorLink);
          }

          // only show the bubble menu for links.
          return (from === to && editorLink) ?? false;
        }}
      >
        {link && (
          <Or2Card or2Variant="inner" sx={{ padding: 1 }}>
            <Grid
              container
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="nowrap"
              spacing={1}
            >
              <Grid>
                <Or2Link
                  isExternal
                  href={link ?? ''}
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'inline-block',
                    verticalAlign: 'middle',
                    maxWidth: 'min(63vw, 400px)',
                    WebkitLineClamp: '2',
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {link}
                </Or2Link>
              </Grid>
              <Grid
                container
                alignItems="center"
                justifyContent="flex-end"
                spacing={0}
              >
                <Grid>
                  <Tooltip title="Edit link">
                    <IconButton
                      size="small"
                      onClick={() => setAnchorDialogOpen(true)}
                    >
                      <EditOutlined />
                    </IconButton>
                  </Tooltip>
                </Grid>
                <Grid>
                  <Tooltip title="Delete link">
                    <IconButton
                      size="small"
                      onClick={() =>
                        editor
                          ?.chain()
                          .focus()
                          .extendMarkRange('link')
                          .unsetLink()
                          .run()
                      }
                    >
                      <DeleteOutlineOutlined />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
            </Grid>
          </Or2Card>
        )}
      </BubbleMenu>
      <AnchorDialog
        rootOwner={rootOwner}
        open={anchorDialogOpen}
        disableFileSelector={disableFileSelector}
        editor={editor}
        onClose={() => setAnchorDialogOpen(false)}
      />
    </div>
  );
};
